<template>
  <ActivityListLayout v-if="links">
    <template v-if="bannerUrl" v-slot:header>
      <ActivityListBanner :bannerUrl="bannerUrl" />
    </template>
    <template v-slot:title>
      <ActivityTitleSection
        :title="listTexts?.title"
        :description="listTexts?.description"
      />
    </template>
    <template v-if="showFilters" v-slot:filter>
      <FilterBarResponsive>
        <template v-slot:search>
          <Search v-model="searchQuery" />
        </template>
        <template v-slot:filters>
          <template v-for="filter in linkFilters" :key="filter.id">
            <FilterSection
              v-if="filter.show"
              :id="filter.id"
              :options="filter.options"
              :title="filter.title"
            />
          </template>
          <template v-for="filter in studyProgramFilters" :key="filter.id">
            <FilterSection
              v-if="filter.show"
              :id="filter.id"
              :options="filter.options"
              :title="filter.title"
            />
          </template>
        </template>
      </FilterBarResponsive>
    </template>
    <template v-slot:content>
      <template v-if="links && links.length === 0">
        <ActivityListNoLinks />
      </template>
      <template v-else-if="view === 'byMonth'">
        <LinksByMonth
          :links
          :activityLocations
          :showType="!activityTypeId"
          :searchQuery
          :linkFilter
          :studyProgramFilter
        />
      </template>
      <template v-else-if="view === 'byStudyPrograms'">
        <LinksByStudyPrograms
          v-if="studyPrograms"
          :links
          :studyPrograms
          :activityLocations
          :showType="!activityTypeId"
          :linkFilter
          :searchQuery
          :studyProgramFilter
          :studyProgramId
        />
      </template>
    </template>
    <ConsentBanner v-if="gtmCode" :tenant="app.tenant.uri" :gtmCode="gtmCode" />
  </ActivityListLayout>
</template>

<script setup lang="ts">
import { eduConfigurationServiceClient } from "@/services/eduConfigurationService.client.service";
import { computed, onBeforeMount, ref } from "vue";
import {
  ApiException,
  PublicActivityLocationDTO,
  PublicActivityPageSettingsLocalizationDTO,
  PublicActivityRegistrationLinkDTO,
  PublicStudyProgramDto,
} from "lib/eduConfigurationServiceClient";
import ActivityTitleSection from "@/views/components/ActivityListTitleSection.vue";
import ConsentBanner from "@/views/components/consent-banner/ConsentBanner.vue";
import ActivityListLayout from "@/views/components/ActivityListLayout.vue";
import ActivityListBanner from "@/views/components/ActivityListBanner.vue";
import { ActivityListRouteProps } from "@/router/index";
import {
  app,
  loadAppTenant,
  setAppColor,
  setAppCulture,
  setAppFavicon,
  setAppPageWidth,
  setAppTitle,
} from "@/app.context";
import FilterBarResponsive from "@/views/components/filters/FilterBarResponsive.vue";
import LinksByStudyPrograms from "@/views/components/by-study-programs/LinksByStudyPrograms.vue";
import LinksByMonth from "@/views/components/by-month/LinksByMonth.vue";
import { filterLinksByQueryParams } from "@/views/query-param-filter";
import Search from "@/components/Search.vue";
import FilterSection from "@/views/components/filters/FilterSection.vue";
import { FilterObject } from "@/views/components/filters/FilterSection.types.";
import createLinkFilter from "@/views/link-filter";
import createStudyProgramFilter from "@/views/studyprogram-filter";
import ActivityListNoLinks from "@/views/components/ActivityListNoLinks.vue";

const props = defineProps<ActivityListRouteProps>();

const listTexts = ref<PublicActivityPageSettingsLocalizationDTO>();
const bannerUrl = ref<string>();
const gtmCode = ref<string>();

const links = ref<PublicActivityRegistrationLinkDTO[]>();
const studyPrograms = ref<PublicStudyProgramDto[]>();
const activityLocations = ref<PublicActivityLocationDTO[]>();

const studyProgramExpanded = ref<Record<string, boolean>>({});

const linkFilters = ref<FilterObject<PublicActivityRegistrationLinkDTO>[]>([]);
const studyProgramFilters = ref<FilterObject<PublicStudyProgramDto>[]>([]);

onBeforeMount(async () => {
  await loadAppTenant(props.tenant);

  if (props.culture) {
    setAppCulture(props.culture);
  }

  await loadSettings(props.activityTypeId);

  const [
    _settings,
    _links,
    _localizations,
    _studyPrograms,
    _activityLocations,
  ] = await Promise.all([
    loadSettings(props.activityTypeId),
    eduConfigurationServiceClient.getPublicRegistrationLinks(),
    eduConfigurationServiceClient.getPublicStudyProgramMetaDataLocalizations(),
    eduConfigurationServiceClient.getPublicStudyPrograms(),
    eduConfigurationServiceClient.getPublicActivityLocations(),
  ]);

  if (_settings?.color) {
    setAppColor(_settings.color);
  }

  if (_settings?.pageWidth) {
    setAppPageWidth(_settings.pageWidth);
  }

  if (_settings?.faviconUrl) {
    setAppFavicon(_settings.faviconUrl);
  }

  listTexts.value = _settings?.localizations.find(
    (localization) => localization.locale === app.culture,
  );

  if (listTexts.value?.title) {
    setAppTitle(listTexts.value.title);
  }

  bannerUrl.value = _settings?.bannerUrl;
  gtmCode.value = _settings?.gtmCode;

  links.value = filterLinksByQueryParams(_links, _studyPrograms, {
    activityTypeId: props.activityTypeId,
    studyProgramId: props.studyProgramId,
    areaOfInterestId: props.areaOfInterestId,
    studyProgramLanguage: props.studyProgramLanguage,
  });

  studyPrograms.value = _studyPrograms.filter((i) =>
    links.value?.flatMap((a) => a.studyProgramIds).includes(i.id),
  );

  activityLocations.value = _activityLocations.filter((i) =>
    links.value?.flatMap((a) => a.activityLocationIds).includes(i.id),
  );

  studyProgramExpanded.value = Object.fromEntries(
    _studyPrograms.map((sp) => [sp.id, false]),
  );

  linkFilters.value = createLinkFilter(links.value, activityLocations.value);
  studyProgramFilters.value = createStudyProgramFilter(
    studyPrograms.value,
    _localizations,
  );
});

async function loadSettings(activityTypeId?: string) {
  const settingList = await eduConfigurationServiceClient
    .getPublicActivityPageSettings()
    .catch(ignore404);

  if (!settingList) return;

  const defaultSetting = settingList.find((s) => !s.activityTypeId);
  if (!activityTypeId) return defaultSetting;

  return (
    settingList.find((s) => s.activityTypeId == activityTypeId) ??
    defaultSetting
  );

  function ignore404(err: Error) {
    if (ApiException.isApiException(err) && err.status === 404) {
      return undefined;
    }
    throw err;
  }
}

const searchQuery = ref<string>();

const noLinkFilterChecked = computed(() =>
  linkFilters.value.every((filter) => !filter.checked.length),
);

const linkFilter = computed(() => {
  if (noLinkFilterChecked.value) return undefined;
  if (!links.value) return undefined;

  let results = links.value;
  for (const filter of linkFilters.value) {
    results = results.filter(filter.matches(filter.checked));
  }

  return results;
});

const noStudyProgramFilterChecked = computed(() =>
  studyProgramFilters.value.every((filter) => !filter.checked.length),
);

const studyProgramFilter = computed(() => {
  if (noStudyProgramFilterChecked.value) return undefined;
  if (!studyPrograms.value) return undefined;

  let results = studyPrograms.value;
  for (const filter of studyProgramFilters.value) {
    results = results.filter(filter.matches(filter.checked));
  }

  return results;
});

const showFilterLinksThreshold = 10;

const showFilters = computed(
  () =>
    !props.studyProgramId &&
    links.value &&
    links.value.length > showFilterLinksThreshold &&
    hasMinimalAvailableFilters.value,
);

const hasMinimalAvailableFilters = computed(() => {
  return (<{ show: boolean }[]>linkFilters.value)
    .concat(studyProgramFilters.value)
    .some((filter) => filter.show);
});
</script>
